import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { Button, Dropdown, Panel, TextInput } from 'src/UIKit/components';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { editCompanyValidationSchema, ValidationErrors } from 'src/constants/validations';
import { isActionAllowed } from 'src/helpers/permissions';
import { useAdminCompaniesApi } from 'src/modules/companies/api';
import { StripeCompanyPortalLink } from 'src/modules/companies/components';
import { AdminCompany } from 'src/modules/companies/types';
import { useCurrentCompany } from 'src/modules/companies/hooks';
import { selectProfile } from 'src/modules/profile/slices';
import * as Yup from 'yup';
import CustomFields from '../CustomFields';
import { settingsTabPanelSx } from '../../helpers';

interface CompanyInfoProps {
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

const CompanyInfo = ({ onUnsavedChangesChange }: CompanyInfoProps) => {
  const { profile } = useSelector(selectProfile);
  const { company: adminCompany, refetch: refetchCompany } = useCurrentCompany();
  const { updateAdminCompany } = useAdminCompaniesApi();

  const [company, setCompany] = useState<AdminCompany>();
  const [originalCompany, setOriginalCompany] = useState<AdminCompany>();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [companySnapshot, setCompanySnapshot] = useState<string | null>(null);
  const [customFieldsRefreshToken, setCustomFieldsRefreshToken] = useState(0);

  const timeZones = moment.tz.zonesForCountry('US');

  const profileRole = useMemo(() => (profile?.role ? profile.role.name : 'bhi_admin'), [profile]);
  const isEditDisabled = profileRole !== 'bhi_admin' && profileRole !== 'company_admin';

  // Create a snapshot of the tracked company fields for comparison
  const createCompanySnapshot = useCallback((companyData: AdminCompany) => {
    return JSON.stringify({
      name: companyData.name,
      timezone: companyData.timezone,
      billingEmail: companyData.billingEmail,
    });
  }, []);

  useEffect(() => {
    if (!adminCompany) {
      return;
    }

    setCompany(adminCompany);
    setOriginalCompany(adminCompany);
    setHasUnsavedChanges(false);
    setCompanySnapshot(createCompanySnapshot(adminCompany));
  }, [adminCompany, createCompanySnapshot]);

  // Notify parent component of unsaved changes
  useEffect(() => {
    if (onUnsavedChangesChange && company) {
      const hasChanges = companySnapshot !== null && companySnapshot !== createCompanySnapshot(company);
      onUnsavedChangesChange(hasChanges);
    }
  }, [company, companySnapshot, createCompanySnapshot, onUnsavedChangesChange]);

  // Check for changes in tracked fields
  const checkForChanges = useCallback((updatedCompany: AdminCompany) => {
    if (!originalCompany) return false;
    
    const hasChanges = 
      updatedCompany.name !== originalCompany.name ||
      updatedCompany.timezone !== originalCompany.timezone ||
      updatedCompany.billingEmail !== originalCompany.billingEmail;
    
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  }, [originalCompany]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setCompany((prevState: AdminCompany | undefined) => {
      if (!prevState) return prevState;
      const updatedCompany = {
        ...prevState,
        [name]: value?.trim(),
      };
      checkForChanges(updatedCompany);
      return updatedCompany;
    });
  };

  const handleChangeTimezone = (timezone: string) => {
    setCompany((prevState: AdminCompany | undefined) => {
      if (!prevState) return prevState;
      const updatedCompany = {
        ...prevState,
        timezone,
      };
      checkForChanges(updatedCompany);
      return updatedCompany;
    });
  };

  const validate = (values: {
    applicationId: string;
    name: string;
    timezone: string;
    billingEmail: string | null;
  }) => {
    try {
      editCompanyValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const isValidForm = (values: {
    applicationId: string;
    name: string;
    timezone: string;
    billingEmail?: string;
  }) => {
    try {
      editCompanyValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      return true;
    } catch {
      return false;
    }
  };

  if (!company) {
    return null;
  }

  const handleCompanySave = () => {
    const isValid = validate({
      applicationId: company.application.id,
      name: company.name,
      timezone: company.timezone,
      billingEmail: company.billingEmail || null,
    });
    if (isValid) {
      updateAdminCompany(
        {
          name: company.name,
          applicationId: company.application.id,
          timezone: company.timezone,
          messagingProfileId: company.messagingProfileId,
          tcrCampaignId: company.tcrCampaignId,
          manualBilling: company.manualBilling,
          billingEmail: company.billingEmail,
          isDemo: company.isDemo,
          sandBoxMode: company.sandBoxMode,
        },
        company.id,
      ).then(() => {
        toast.success('Company is updated');
        setOriginalCompany(company);
        setHasUnsavedChanges(false);
        setCompanySnapshot(createCompanySnapshot(company));
        if (onUnsavedChangesChange) {
          onUnsavedChangesChange(false);
        }
        refetchCompany();
        setCustomFieldsRefreshToken(prev => prev + 1);
      });
    }
  };



  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 2fr)' }}
      alignItems="start"
      width="100%"
      gap="20px"
    >
      <Panel>
        <Stack gap={2}>
          <TextInput
            disabled={profileRole !== 'bhi_admin'}
            label="Name"
            name="name"
            value={company.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors?.name || ''}
            margin="none"
          />

          <Dropdown
            disabled={isEditDisabled}
            label="Time zone"
            value={company.timezone}
            options={timeZones.map(item => ({ value: item, title: item, entity: item }))}
            onChange={(_, value) => handleChangeTimezone(value)}
            margin="none"
          />

          <Typography variant="body2">
            {company.phoneNumbers.length === 0 ? 'There are no phone numbers' : 'Phone Number(s):'}
          </Typography>

          {company.phoneNumbers.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {company.phoneNumbers.map(p => (
                <Chip
                  key={p.id}
                  label={p.number}
                />
              ))}
            </Box>
          )}

          <TextInput
            disabled={isEditDisabled}
            label="Billing Email"
            name="billingEmail"
            value={company.billingEmail || undefined}
            onChange={handleChange}
            error={!!errors.billingEmail}
            helperText={errors?.billingEmail || ''}
            margin="none"
          />
          {!isEditDisabled && <StripeCompanyPortalLink disabled={false} />}

          {!isEditDisabled && (
            <Box display="flex" justifyContent="end">
              <Button
                variant="contained"
                disabled={
                  !hasUnsavedChanges ||
                  !isValidForm({
                    applicationId: company.application.id,
                    name: company.name,
                    timezone: company.timezone,
                    billingEmail: company.billingEmail,
                  })
                }
                color={hasUnsavedChanges ? "primary" : "inherit"}
                onClick={handleCompanySave}
              >
                Save
              </Button>
            </Box>
          )}
        </Stack>
      </Panel>
      <Box sx={{ ...settingsTabPanelSx, minWidth: 0, minHeight: 400 }}>
        <CustomFields
          profileRole={profileRole}
          isCompanyFields={true}
          showButtons={isActionAllowed({ authorRole: profileRole })}
          refreshToken={customFieldsRefreshToken}
        />
      </Box>
    </Box>
  );
};

export default CompanyInfo;
